import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  backendApiEndpoints,
  GetCheckoutPageUrlParams
} from "./backend-api/backend-api";
import { CommonRootState } from "@common/types/common-root-state-type";
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { AnalyticsService } from "@common-services/analytics-service";

// Interfaces for credit package purchase
export interface CreditPackagePricing {
  price: number;
  perCredit: number;
  billingCycle: 'monthly' | 'annual';
  id: string; // The Chargebee item price ID
}

export interface CreditPackage {
  credits: number;
  monthly?: CreditPackagePricing;
  annual?: CreditPackagePricing;
  popular?: boolean;
  savePercentage?: number;
  description?: string;
  name?: string;
  // One-time credit top-up (serenix-com/Blueticks#143 option b). A top-up is a single
  // one-time charge with NO billing cycle — so `monthly`/`annual` carry the same price+id
  // (the `{N}-Credits-USD` charge item) and the modal hides the cycle toggle for it.
  oneTime?: boolean;
}

// New interfaces for subscription plans
export interface SubscriptionPlanPricing {
  price: number;
  credits: number;
  planId: string;
  billingCycle: 'monthly' | 'annual';
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  features: string[];
  monthly?: SubscriptionPlanPricing;
  annual?: SubscriptionPlanPricing;
  popular?: boolean;
}

export interface PurchaseCreditPackageParams {
  packageId: string; // The Chargebee item price ID
  quantity?: number;
}

// Type guard to check if the result has an error
function isErrorResult(result: any): result is { error: FetchBaseQueryError | SerializedError } {
  return result && 'error' in result;
}

// Type guard to check if the result has data
function isDataResult<T>(result: any): result is { data: T } {
  return result && 'data' in result;
}

/**
 * Organize one-time credit top-up options (serenix-com/Blueticks#143 option b) into the
 * CreditPackage shape the modal renders. A top-up has NO billing cycle, so the single
 * one-time price+id (`{N}-Credits-USD`) is placed in both the monthly and annual slots
 * (identical), each package is flagged `oneTime`, and the modal hides the cycle toggle.
 */
function organizeTopUpOptions(options: any[]): CreditPackage[] {
  return options
    // credits + a positive price: never render a $0 (misconfigured/gift) item as a free top-up (#143)
    .filter((o) => o && o.credits > 0 && o.id && o.price > 0)
    .map((o) => {
      const pricing: CreditPackagePricing = { price: o.price, perCredit: o.perCredit, billingCycle: 'monthly', id: o.id };
      return {
        credits: o.credits,
        name: o.name?.replace(/ (USD|Monthly|Yearly).*$/i, '') || `${o.credits} Credits`,
        description: o.description || '',
        popular: o.popular || false,
        oneTime: true,
        monthly: pricing,
        annual: { ...pricing, billingCycle: 'annual' },
      } as CreditPackage;
    })
    .sort((a, b) => a.credits - b.credits);
}

// Thunk to initiate the checkout process for credit packages
export const purchaseCreditPackageThunk = createAsyncThunk<
  { checkoutUrl: string }, 
  PurchaseCreditPackageParams,
  { state: CommonRootState; rejectValue: string }
>(
  'credits/purchaseCreditPackage',
  async (params, thunkApi) => {
    try {
      const { packageId, quantity = 1 } = params;
      console.log(`Initiating checkout for package ID: ${packageId}, quantity: ${quantity}`);

      // Fetch fresh GA4 session data for server-side purchase event linking
      const analyticsService = new AnalyticsService();
      const freshSessionId = await analyticsService.getGA4SessionId();
      const gaClientId = await analyticsService.getGA4ClientId();

      // Use the proper getCheckoutPageUrl endpoint
      const result = await thunkApi.dispatch(
        backendApiEndpoints.getCheckoutPageUrl.initiate({
          // The one-time credit charge item (`{N}-Credits-USD`).
          item_price_id: packageId,
          quantity: quantity,
          // ONE-TIME TOP-UP (serenix-com/Blueticks#143 option b). The backend appends this
          // one-time charge item to the current item list (plan + all add-ons kept),
          // invoices it once, and grants credits from the item's `credits` entitlement.
          // REPEATABLE — no owned-pack guard. (New-subscription plan purchases go through
          // handleSubscriptionCheckout and leave this unset → replace semantics.)
          credit_topup: true,
          ...(freshSessionId && { ga_session_id: freshSessionId }),
          ...(gaClientId && { ga_client_id: gaClientId }),
        } as GetCheckoutPageUrlParams)
      );
      
      // Handle the RTK Query result
      if (isErrorResult(result)) {
        console.error('Error from checkout endpoint:', result.error);
        const errorMessage = typeof result.error === 'object' && 'message' in result.error 
          ? result.error.message 
          : 'Failed to fetch checkout URL';
        return thunkApi.rejectWithValue(errorMessage as string);
      }
      
      if (!isDataResult(result) || !result.data) {
        return thunkApi.rejectWithValue('Invalid response from checkout URL endpoint');
      }
      
      const response = result.data;
      console.log('Checkout API response:', response);
      
      if (!response || !response.checkoutPageUrl) {
        console.error('No checkout URL in response:', response);
        return thunkApi.rejectWithValue('No checkout URL returned from server');
      }
      
      console.log('Successfully retrieved checkout URL:', response.checkoutPageUrl);
      return {
        checkoutUrl: response.checkoutPageUrl
      };
    } catch (error) {
      console.error('Error initiating credit package purchase:', error);
      const errMsg = error instanceof Error ? error.message : String(error);
      return thunkApi.rejectWithValue(errMsg || 'Failed to initiate purchase');
    }
  }
);

// Thunk to get available credit packages from the backend
export const fetchCreditPackagesThunk = createAsyncThunk<
  CreditPackage[],
  void,
  { state: CommonRootState; rejectValue: string }
>(
  'credits/fetchPackages',
  async (_, thunkApi) => {
    try {
      console.log('Fetching credit packages from Chargebee...');
      
      // One-time credit top-up options (serenix-com/Blueticks#143 option b) — charge items
      // whose credit amount comes from their `credits` entitlement. Replaces getAddOns
      // (recurring packs) for NEW purchases; existing pack owners are left as-is.
      const result = await thunkApi.dispatch(
        backendApiEndpoints.createEntity.initiate({
          service: 'actions',
          entity: {
            cmd: 'chargebee.getCreditTopUpOptions'
          }
        })
      );
      
      // Handle the RTK Query result with proper type checking
      if (isErrorResult(result)) {
        console.error('Error from add-ons endpoint:', result.error);
        const errorMessage = typeof result.error === 'object' && 'message' in result.error 
          ? result.error.message 
          : 'Failed to fetch credit packages';
        return thunkApi.rejectWithValue(errorMessage as string);
      }
      
      if (!isDataResult(result) || !result.data) {
        return thunkApi.rejectWithValue('Invalid response from add-ons endpoint');
      }
      
      const response = result.data;
      console.log('Response from Chargebee credit top-up options API:', response);

      // Check if we have valid top-up options
      if (response && Array.isArray(response) && response.length > 0) {
        console.log(`Found ${response.length} credit top-up options:`, response);

        // Organize the one-time top-up options into structured packages
        const organizedPackages = organizeTopUpOptions(response);
        console.log('Organized top-up packages:', organizedPackages);

        if (organizedPackages.length > 0) {
          return organizedPackages;
        }
      }
      
      // Fallback to static packages if API call fails or returns empty
      console.warn('No add-ons found or invalid response format, using fallback data');
      const fallbackData: CreditPackage[] = [
        { 
          credits: 100, 
          monthly: { price: 7.99, perCredit: 0.08, id: 'small-monthly', billingCycle: 'monthly' },
          annual: { price: 81.50, perCredit: 0.068, id: 'small-annual', billingCycle: 'annual' }
        },
        { 
          credits: 200, 
          monthly: { price: 14.99, perCredit: 0.07, id: 'medium-monthly', billingCycle: 'monthly' },
          annual: { price: 152.90, perCredit: 0.064, id: 'medium-annual', billingCycle: 'annual' },
          popular: true 
        },
        { 
          credits: 500, 
          monthly: { price: 34.99, perCredit: 0.07, id: 'large-monthly', billingCycle: 'monthly' },
          annual: { price: 356.90, perCredit: 0.059, id: 'large-annual', billingCycle: 'annual' }
        },
        { 
          credits: 1000, 
          monthly: { price: 64.99, perCredit: 0.06, id: 'extra-monthly', billingCycle: 'monthly' },
          annual: { price: 662.90, perCredit: 0.055, id: 'extra-annual', billingCycle: 'annual' }
        }
      ];
      return fallbackData;
    } catch (error) {
      console.error('Error fetching credit packages:', error);
      
      // Return specific error message
      if (error instanceof Error && error.message) {
        return thunkApi.rejectWithValue(error.message);
      }
      
      // Return fallback data on error
      const fallbackData: CreditPackage[] = [
        { 
          credits: 100, 
          monthly: { price: 7.99, perCredit: 0.08, id: 'small-monthly', billingCycle: 'monthly' },
          annual: { price: 81.50, perCredit: 0.068, id: 'small-annual', billingCycle: 'annual' }
        },
        { 
          credits: 200, 
          monthly: { price: 14.99, perCredit: 0.07, id: 'medium-monthly', billingCycle: 'monthly' },
          annual: { price: 152.90, perCredit: 0.064, id: 'medium-annual', billingCycle: 'annual' },
          popular: true 
        },
        { 
          credits: 500, 
          monthly: { price: 34.99, perCredit: 0.07, id: 'large-monthly', billingCycle: 'monthly' },
          annual: { price: 356.90, perCredit: 0.059, id: 'large-annual', billingCycle: 'annual' }
        },
        { 
          credits: 1000, 
          monthly: { price: 64.99, perCredit: 0.06, id: 'extra-monthly', billingCycle: 'monthly' },
          annual: { price: 662.90, perCredit: 0.055, id: 'extra-annual', billingCycle: 'annual' }
        }
      ];
      return fallbackData;
    }
  }
); 